import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { usePermissions } from './usePermissions'

export interface AssumptionExclusion {
  id: string
  organizationId: string
  title: string
  description: string
  category: 'Assumption' | 'Exclusion'
  isDefault: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface AssumptionExclusionFilters {
  category?: string
  isActive?: boolean
}

export function useAssumptionsExclusions(filters?: AssumptionExclusionFilters) {
  const { organization } = useAuth()
  const { can } = usePermissions()
  return useQuery({
    queryKey: ['assumptionsExclusions', organization?.id, filters],
    queryFn: async () => {
      const params = new URLSearchParams({ organizationId: organization?.id || '' })
      if (filters?.category) params.set('category', filters.category)
      if (filters?.isActive !== undefined) params.set('isActive', String(filters.isActive))
      const res = await api.get<any>(`/master-data/assumptions-exclusions?${params}`)
      return (res?.data || []) as AssumptionExclusion[]
    },
    enabled: !!organization?.id && can('QUOTATIONS', 'VIEW'),
    staleTime: 5 * 60 * 1000,
  })
}

export function useAssumptionExclusionMutations() {
  const { organization } = useAuth()
  const queryClient = useQueryClient()
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['assumptionsExclusions', organization?.id] })
  }

  const create = useMutation({
    mutationFn: (payload: Omit<AssumptionExclusion, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) =>
      api.post<any>('/master-data/assumptions-exclusions', { ...payload, organizationId: organization?.id }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<AssumptionExclusion> }) =>
      api.patch<any>(`/master-data/assumptions-exclusions/${id}`, payload, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const setStatus = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch<any>(`/master-data/assumptions-exclusions/${id}/status`, { isActive }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<any>(`/master-data/assumptions-exclusions/${id}`, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const setDefault = useMutation({
    mutationFn: (id: string) =>
      api.post<any>(`/master-data/assumptions-exclusions/${id}/set-default`, {}, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  return { create, update, setStatus, remove, setDefault }
}